import { showRequestError } from '../../utils/request-feedback';
import { PermissionControl } from '../../permissions/access';
import { useEffect, useState } from "react";
import { Pagination, Button, Popconfirm, Table, Typography, Tag, Flex } from "antd";
import PostReview from "../../components/community/post_review";
import dayjs from "dayjs";
import UserInfoView from "../../components/community/user_info_view";
import DailyInsightsEditDrawer from "../../components/daily/insights_draw_view";
import { deleteDailyInsights, fetchDailyInsights, updateDailyInsights } from "../../api/ai";
import { PlusOutlined } from "@ant-design/icons";

const DailyInsightsScreen = () => {
    const [posts, setPosts] = useState([]);
    const [open, setOpen] = useState(false);
    const [currPost, setCurrPost] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [searchParams, setSearchParams] = useState({
        title: '',
        status: '',
    });
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const tabCols = [
        {
            title: 'Index',
            dataIndex: 'id',
            key: 'id',
            render: (text, _, index) => ((pageNum - 1) * pageSize + index + 1)
        },
        {
            title: 'Column',
            dataIndex: 'columnName',
            key: 'columnName',
            render: (text, _) => {
                return <Tag>{text}</Tag>
            },
        },
        {
            title: 'PostId',
            dataIndex: 'postId',
            key: 'postId',
            render: (text, _) => {
                return <div style={{ width: 80 }}><Typography.Text copyable style={{ fontSize: 11, color: '#afafaf' }}>{text}</Typography.Text></div>
            },
        },
        {
            title: 'Title',
            dataIndex: 'articleTitle',
            key: 'articleTitle',
            render: (text, _) => {
                return <b>{text}</b>
            },
        },
        {
            title: 'Weight',
            dataIndex: 'weight',
            key: 'weight',
            render: (text, _) => {
                if (!text) return '-';
                return <b>{text}</b>
            },
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text, _) => {
                return <label style={{ fontSize: 12, color: '#afafaf', whiteSpace: 'nowrap' }}>{dayjs(text).format('MM-DD HH:mm')}</label>
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (text, _) => {
                if (String(text).toLowerCase() === '1') {
                    return <Tag color="green">Active</Tag>
                }
                return <Tag color="red">Inactive</Tag>
            },
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (text, record) => {
                return (
                    <Button.Group size='small'>
                        <PermissionControl permission="dailyinsights.control.activeHandle.ccda8b52"><Button type='link' onClick={() => activeHandle(record)}>
                            {
                                record.status === 0 ? 'Active' : 'Inactive'
                            }
                        </Button></PermissionControl>
                        <PermissionControl permission="dailyinsights.control.editHandle.9eb984c0"><Button type='link' onClick={() => { editHandle(record) }}>Edit</Button></PermissionControl>
                        <PermissionControl permission="dailyinsights.control.deleteHandle.304452f8"><Popconfirm
                            title="Delete Action"
                            description="Are you sure to remove this item?"
                            onConfirm={() => { deleteHandle(record) }}
                            okText="Yes"
                            cancelText="No" >
                            <Button danger type='link'>delete</Button>
                        </Popconfirm></PermissionControl>
                    </Button.Group>
                )
            },
        }
    ];

    useEffect(() => {
        getData(pageNum, pageSize, searchParams);
    }, [])

    const getData = (pNum, pSize, search) => {
        setLoading(true);
        let params = {
            'page.num': pNum,
            'page.size': pSize,
            ...search
        }
        fetchDailyInsights(params).then(res => {
            if (res.list) {
                setPosts(res.list);
            }
            if (res.page) {
                setTotal(res.page.total);
            }
            setLoading(false);
        }).catch(showRequestError).finally(() => { setLoading(false); })
    }

    const editHandle = (record) => {
        setCurrPost(record);
        setOpen(true);
    }

    const addNewHandle = (record) => {
        setCurrPost(null);
        setOpen(true);
    }

    const deleteHandle = (record) => {
        setLoading(true);
        deleteDailyInsights(record.id).then(res => {
            getData(pageNum, pageSize, searchParams);
        }).finally(() => {
            setLoading(false)
        }).catch(showRequestError)
    }

    const activeHandle = (record) => {
        setLoading(true);
        updateDailyInsights({ id: record.id, status: record.status === 1 ? 0 : 1 }).then(res => {
            if (res) {
                getData(pageNum, pageSize);
            }
        }).finally(() => {
            setLoading(false);
        }).catch(showRequestError)
    }

    const reviewHandle = (record) => {
        setCurrPost(record);
        setReviewOpen(true);
    }

    const pageChange = (page, size) => {
        setPageNum(page);
        setPageSize(size);
        getData(page, size, searchParams);
    }

    return (
        <div className='main-wrapper'>
            <div className='top-part cnt-item'>
                <Flex justify='space-between'>
                    <PermissionControl permission="dailyinsights.control.button.9d60a9f0"><Button type='primary' icon={<PlusOutlined />} onClick={addNewHandle}>Add Daily Insights</Button></PermissionControl>
                </Flex>
            </div>
            <div className='cnt-item'>
                <Table
                    loading={loading}
                    rowKey={(record) => record.id}
                    rowClassName={'table-row'}
                    size='small'
                    dataSource={posts}
                    columns={tabCols}
                    pagination={false}
                    expandable={{ rowExpandable: (record) => false, showExpandColumn: false }} />
            </div>
            <div className='footer-part cnt-item'>
                <Pagination
                    total={total}
                    showTotal={(total) => `total ${total}`}
                    current={pageNum}
                    pageSize={pageSize}
                    showSizeChanger={true}
                    pageSizeOptions={[10, 20, 30, 50]}
                    onChange={(page, size) => pageChange(page, size)}
                />
            </div>
            <DailyInsightsEditDrawer
                isVideo={false}
                open={open}
                defaultPost={currPost}
                onClose={() => setOpen(false)}
                onUpdate={() => getData(pageNum, pageSize, searchParams)} />
            <PostReview
                from="dailyinsightpost"
                open={reviewOpen}
                post={currPost ? currPost.post : null}
                onClose={() => setReviewOpen(false)}
                canEdit={false} />
        </div>
    );
}
export default DailyInsightsScreen;